import React, { useState, useEffect } from 'react';
import { getSilos, createSilo, updateSilo, getSiloMovements } from '../../lib/api';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Card, CardContent } from '../../components/ui/card';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Plus, Loader2, Cylinder, ArrowRight, Edit, Save, Trash2 } from 'lucide-react';

const empty = { name: '', code: '', capacity_litres: '', status: 'active', notes: '' };
const MOVEMENT_LABELS = {
  intake_to_silo: 'Intake → Silo',
  silo_to_make_day: 'Silo → Make Day',
  silo_transfer: 'Silo transfer',
  adjustment: 'Adjustment',
  cleaning: 'Cleaning',
};

export default function Silos() {
  const [silos, setSilos] = useState([]);
  const [movements, setMovements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  // Movement filters + edit
  const [filterSilo, setFilterSilo] = useState('all');
  const [filterType, setFilterType] = useState('all');
  const [filterFrom, setFilterFrom] = useState('');
  const [filterTo, setFilterTo] = useState('');
  const [movDialog, setMovDialog] = useState(false);
  const [movEditing, setMovEditing] = useState(null);
  const [movForm, setMovForm] = useState({ movement_date: '', movement_type: 'adjustment', source_silo_id: '', dest_silo_id: '', quantity_litres: 0, notes: '' });

  useEffect(() => { load(); }, []);
  const load = async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filterSilo !== 'all') params.silo_id = filterSilo;
      if (filterType !== 'all') params.movement_type = filterType;
      if (filterFrom) params.date_from = filterFrom;
      if (filterTo) params.date_to = filterTo;
      const [s, m] = await Promise.all([getSilos(), getSiloMovements(params)]);
      setSilos(s.data); setMovements(m.data);
    } catch (e) { toast.error('Failed to load silos'); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterSilo, filterType, filterFrom, filterTo]);

  const openNew = () => { setEditing(null); setForm(empty); setDialog(true); };
  const openEdit = (s) => { setEditing(s); setForm({ ...empty, ...s }); setDialog(true); };

  const save = async () => {
    if (!form.name?.trim()) { toast.error('Name required'); return; }
    setSaving(true);
    try {
      const payload = { ...form, capacity_litres: form.capacity_litres ? Number(form.capacity_litres) : undefined };
      if (editing) await updateSilo(editing.id, payload);
      else await createSilo(payload);
      toast.success('Saved'); setDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setSaving(false); }
  };

  const siloName = (id) => silos.find(s => s.id === id)?.name || '—';

  const openMovEdit = (m) => {
    setMovEditing(m);
    setMovForm({
      movement_date: m.movement_date || '',
      movement_type: m.movement_type || 'adjustment',
      source_silo_id: m.source_silo_id || '',
      dest_silo_id: m.dest_silo_id || '',
      quantity_litres: m.quantity_litres || 0,
      notes: m.notes || '',
    });
    setMovDialog(true);
  };
  const saveMov = async () => {
    try {
      const payload = { ...movForm, quantity_litres: Number(movForm.quantity_litres) || 0 };
      await api.put(`/silos/movements/${movEditing.id}`, payload);
      toast.success('Movement updated');
      setMovDialog(false); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
  };
  const deleteMov = async (m) => {
    if (!window.confirm('Delete this movement record?')) return;
    try { await api.delete(`/silos/movements/${m.id}`); toast.success('Deleted'); load(); }
    catch (e) { toast.error('Delete failed'); }
  };

  return (
    <div className="space-y-4" data-testid="silos-page">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl font-semibold flex items-center gap-2"><Cylinder className="w-5 h-5" />Silos</h1>
          <p className="text-sm text-muted-foreground">Inwards · raw-milk storage tanks. Movements (intake → silo, silo → make-day) are recorded automatically.</p>
        </div>
        <Button onClick={openNew} data-testid="btn-new-silo"><Plus className="w-4 h-4 mr-1" />New silo</Button>
      </div>

      {loading ? <div className="flex items-center justify-center py-12"><Loader2 className="w-6 h-6 animate-spin" /></div> :
        <div className="grid md:grid-cols-2 gap-4">
          <Card><CardContent className="p-0">
            <div className="px-4 py-3 border-b text-sm font-semibold">Silo register</div>
            <table className="w-full text-sm">
              <thead className="bg-zinc-50 text-xs uppercase text-zinc-500"><tr><th className="text-left px-4 py-2">Silo</th><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Capacity</th><th className="text-left px-4 py-2">Status</th><th></th></tr></thead>
              <tbody>
                {silos.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No silos yet.</td></tr> :
                  silos.map(s => (
                    <tr key={s.id} className="border-t">
                      <td className="px-4 py-2 font-medium">{s.name}</td>
                      <td className="px-4 py-2 font-mono text-xs">{s.code || '—'}</td>
                      <td className="px-4 py-2">{s.capacity_litres ? `${s.capacity_litres.toLocaleString()} L` : '—'}</td>
                      <td className="px-4 py-2"><Badge variant={s.status === 'active' ? 'default' : 'outline'}>{s.status}</Badge></td>
                      <td className="px-4 py-2 text-right"><Button size="sm" variant="ghost" onClick={() => openEdit(s)}><Edit className="w-4 h-4" /></Button></td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </CardContent></Card>

          <Card><CardContent className="p-0">
            <div className="px-4 py-3 border-b text-sm font-semibold flex items-center justify-between gap-2 flex-wrap">
              <span>Recent movements</span>
              <div className="flex gap-1 flex-wrap text-xs font-normal">
                <Select value={filterSilo} onValueChange={setFilterSilo}>
                  <SelectTrigger className="h-7 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All silos</SelectItem>
                    {silos.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Select value={filterType} onValueChange={setFilterType}>
                  <SelectTrigger className="h-7 w-[140px] text-xs"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All types</SelectItem>
                    {Object.entries(MOVEMENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}
                  </SelectContent>
                </Select>
                <Input type="date" value={filterFrom} onChange={e => setFilterFrom(e.target.value)} className="h-7 w-[130px] text-xs" />
                <Input type="date" value={filterTo} onChange={e => setFilterTo(e.target.value)} className="h-7 w-[130px] text-xs" />
              </div>
            </div>
            <div className="max-h-[420px] overflow-auto">
              <table className="w-full text-sm">
                <thead className="bg-zinc-50 text-xs uppercase text-zinc-500 sticky top-0"><tr><th className="text-left px-4 py-2">Date</th><th className="text-left px-4 py-2">Type</th><th className="text-left px-4 py-2">From → To</th><th className="text-right px-4 py-2">Litres</th><th></th></tr></thead>
                <tbody>
                  {movements.length === 0 ? <tr><td colSpan={5} className="text-center py-8 text-muted-foreground">No movements match these filters.</td></tr> :
                    movements.map(m => (
                      <tr key={m.id} className="border-t">
                        <td className="px-4 py-2 text-xs">{m.movement_date}</td>
                        <td className="px-4 py-2 text-xs"><Badge variant="outline" className="text-[10px]">{MOVEMENT_LABELS[m.movement_type] || m.movement_type}</Badge></td>
                        <td className="px-4 py-2 text-xs">
                          {m.source_silo_id ? siloName(m.source_silo_id) : <span className="text-muted-foreground">intake</span>}
                          <ArrowRight className="inline w-3 h-3 mx-1 text-muted-foreground" />
                          {m.dest_silo_id ? siloName(m.dest_silo_id) : <span className="text-muted-foreground">make-day</span>}
                        </td>
                        <td className="px-4 py-2 text-right font-medium">{m.quantity_litres?.toLocaleString() || '—'}</td>
                        <td className="px-4 py-2 text-right">
                          <Button size="sm" variant="ghost" onClick={() => openMovEdit(m)} data-testid={`btn-edit-mov-${m.id}`}><Edit className="w-3 h-3" /></Button>
                          <Button size="sm" variant="ghost" onClick={() => deleteMov(m)}><Trash2 className="w-3 h-3 text-red-600" /></Button>
                        </td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
          </CardContent></Card>
        </div>}

      <Dialog open={dialog} onOpenChange={setDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit silo' : 'New silo'}</DialogTitle>
            <DialogDescription>Silos are physical milk-storage tanks.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div className="col-span-2"><Label>Name *</Label><Input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="e.g. Silo 2" /></div>
            <div><Label>Code</Label><Input value={form.code} onChange={e => setForm({ ...form, code: e.target.value })} placeholder="S2" /></div>
            <div><Label>Capacity (litres)</Label><Input type="number" value={form.capacity_litres} onChange={e => setForm({ ...form, capacity_litres: e.target.value })} /></div>
            <div className="col-span-2"><Label>Status</Label>
              <Select value={form.status} onValueChange={v => setForm({ ...form, status: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="active">Active</SelectItem><SelectItem value="inactive">Inactive</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialog(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving}>{saving && <Loader2 className="w-4 h-4 animate-spin mr-1" />}Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Movement edit dialog */}
      <Dialog open={movDialog} onOpenChange={setMovDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit silo movement</DialogTitle>
            <DialogDescription>Auto-recorded movements can be corrected here. Use sparingly — most movements are created automatically by milk intake and make-day saves.</DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3 py-2">
            <div><Label>Date</Label><Input type="date" value={movForm.movement_date} onChange={e => setMovForm({ ...movForm, movement_date: e.target.value })} /></div>
            <div><Label>Type</Label>
              <Select value={movForm.movement_type} onValueChange={v => setMovForm({ ...movForm, movement_type: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(MOVEMENT_LABELS).map(([v, l]) => <SelectItem key={v} value={v}>{l}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>From silo</Label>
              <Select value={movForm.source_silo_id || 'none'} onValueChange={v => setMovForm({ ...movForm, source_silo_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— none —</SelectItem>{silos.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>To silo</Label>
              <Select value={movForm.dest_silo_id || 'none'} onValueChange={v => setMovForm({ ...movForm, dest_silo_id: v === 'none' ? '' : v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="none">— none —</SelectItem>{silos.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Litres</Label><Input type="number" value={movForm.quantity_litres} onChange={e => setMovForm({ ...movForm, quantity_litres: e.target.value })} /></div>
            <div className="col-span-2"><Label>Notes</Label><Textarea rows={2} value={movForm.notes} onChange={e => setMovForm({ ...movForm, notes: e.target.value })} /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMovDialog(false)}>Cancel</Button>
            <Button onClick={saveMov}><Save className="w-4 h-4 mr-1" />Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
